import { createContext, useContext, useState } from "react";
import { getEmailPreviewSource } from "./hooks/use-email-preview-source";
import { usePreviewInfo } from "./hooks/use-preview-info";
import { usePreviewTestData } from "./hooks/use-preview-test-data";

import type { UseSupabaseLoaderState } from "../../../../../../server/supabase/hooks/types";
import type { MomentPreviewTags } from "../../../../server/supabase-preview-service";
import type { MomentPreviewInfo } from "./hooks/use-preview-info";
import type { PreviewMomentData } from "./types";
import type { FC, ReactNode } from "react";

export interface PreviewContextType {
  preview_info: UseSupabaseLoaderState<MomentPreviewInfo>;
  preview_test_data: UseSupabaseLoaderState<MomentPreviewTags>;
  previewMode: "desktop" | "mobile";
  setPreviewMode: (mode: "desktop" | "mobile") => void;
  moment_preview_data: PreviewMomentData;
}

const PreviewContext = createContext<PreviewContextType | null>(null);

export const PreviewContextProvider: FC<{ children: ReactNode; data: PreviewMomentData }> = ({
  children,
  data,
}) => {
  const preview_info = usePreviewInfo({
    id: data.id,
    channel: data.channel,
    segment_json: data.segment?.segment_json ?? null,
    target_connection_type: data.target_connection_type,
    dynamic_segment_id_map: data.dynamic_segment_id_map,
    additional_recipients: data.additional_recipients,
    cc_bcc: data.cc_bcc,
    segment_operation: data.segment?.operation ?? null,
    base_segment_name: data.segment?.name ?? null,
    target_channel: data.target_channel,
    target_manager: data.target_manager,
    only_additional_recipients: data.only_additional_recipients,
    notify_channel: data.notify_channel,
  });

  const emailPreviewSource = getEmailPreviewSource({
    channel: data.channel,
    use_lexical_editor: data.use_lexical_editor ?? false,
    design: data.design,
    email_subject: data.email_subject,
    from: data.from,
    email_moment: data.email_moment
      ? {
          subject: data.email_moment.subject ?? "",
          from_address: data.email_moment.from_address ?? "",
          body: data.email_moment.body ?? "",
        }
      : null,
  });

  const preview_test_data = usePreviewTestData(emailPreviewSource);

  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");

  return (
    <PreviewContext.Provider
      value={{
        preview_info,
        preview_test_data,
        previewMode,
        setPreviewMode,
        moment_preview_data: data,
      }}
    >
      {children}
    </PreviewContext.Provider>
  );
};

export const usePreviewContext = (): PreviewContextType => {
  const context = useContext(PreviewContext);
  if (!context) {
    throw new Error("usePreviewContext be used within a PreviewContextProvider");
  }
  return context;
};
